'use client';

import { useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useLocale } from './LocaleProvider';
import { TextReveal } from './primitives/TextReveal';
import { CameraIcon } from './icons/LuxuryIcons';
import { TurnstileWidget } from './TurnstileWidget';
import exifr from 'exifr';
import {
  UI_BRAND_PROTOCOLS,
  getUiProtocol,
  type UiBrandProtocol,
  type UiAngle,
  type BrandProtocolKey,
  type PhotoAngle,
  type AuthenticityResult,
} from '@sophia/shared';

/**
 * AuthenticityScanner — Tier 2 multi-angle, brand-aware wizard.
 *
 * Flow: brand + item → per-angle guided photo capture → optional
 * serial/date-code entry → scan → aggregated verdict.
 *
 * Backend contract:
 *   POST /api/authenticity with
 *     { brand, itemType, photos: [{angle, image, mimeType}], code? }
 *   → AuthenticityResult with per-angle findings + aggregated verdict.
 *
 * Rate-limited 5 scans/hour/IP. Images are not persisted server-side.
 */

const API_BASE = 'https://api.mysophia.tech';

type Step = 'brand' | 'photos' | 'code' | 'consent' | 'scanning' | 'results' | 'error';

interface PhotoConsent {
  /** Tier 1 — default ON. 90-day dispute retention. */
  allow_dispute_storage: boolean;
  /** Tier 2 — default OFF. ML training opt-in. */
  allow_training_use: boolean;
}

interface PhotoMeta {
  captureDate?: string | null;
  lat?: number | null;
  lng?: number | null;
  camera?: string | null;
}

interface CapturedPhoto {
  angle: PhotoAngle;
  image: string; // data URL
  mimeType: string;
  meta?: PhotoMeta;
}

function fileToDataUrl(file: File): Promise<{ image: string; mimeType: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read_failed'));
    reader.onload = () =>
      resolve({ image: reader.result as string, mimeType: file.type || 'image/jpeg' });
    reader.readAsDataURL(file);
  });
}

/**
 * Extract EXIF metadata from an uploaded photo. We only pull the
 * specific fields the authenticity cross-signal needs — capture date,
 * GPS, camera model. Happens entirely in-browser (exifr runs locally),
 * so no raw EXIF leaks to third parties.
 */
async function readPhotoMeta(file: File): Promise<PhotoMeta> {
  try {
    const parsed = await exifr.parse(file, {
      pick: ['DateTimeOriginal', 'GPSLatitude', 'GPSLongitude', 'Make', 'Model'],
      reviveValues: true,
    });
    if (!parsed) return {};
    const captureDate = parsed.DateTimeOriginal instanceof Date
      ? parsed.DateTimeOriginal.toISOString()
      : null;
    const lat = typeof parsed.GPSLatitude === 'number' ? parsed.GPSLatitude : null;
    const lng = typeof parsed.GPSLongitude === 'number' ? parsed.GPSLongitude : null;
    const camera = [parsed.Make, parsed.Model]
      .filter((s): s is string => typeof s === 'string' && s.length > 0)
      .join(' ')
      .slice(0, 80);
    return { captureDate, lat, lng, camera: camera || null };
  } catch {
    // EXIF extraction is best-effort — a missing or corrupt EXIF block
    // should not block the scan.
    return {};
  }
}

// ------------------------------------------------------------------------
// Bilingual copy
// ------------------------------------------------------------------------

function useCopy(isAr: boolean) {
  return useMemo(() => (isAr
    ? {
        ribbon: 'نسخة احترافية',
        eyebrow: 'فحص الأصالة الذكي',
        heading: 'عين خبير. في هاتفك.',
        sub: 'تحليل متعدد الزوايا مدرَّب على بروتوكولات الأصالة لكل ماركة. ستة لقطات، نموذج Claude Opus Vision، تقييم شامل — الصور لا تُخزَّن.',
        step1: 'الخطوة 1 · الماركة',
        step1Heading: 'ما هي القطعة؟',
        step1Sub: 'سنكيّف الفحص بناءً على بروتوكول الأصالة الخاص بالماركة.',
        itemTypeLabel: 'النوع',
        continue: 'متابعة',
        step2: 'الخطوة 2 · الصور',
        step2Heading: 'التقط الصور المطلوبة',
        step2Sub: 'كل زاوية تكشف عن معلومة لا تظهر في الأخرى. المطلوبة ملوّنة بالذهبي.',
        required: 'مطلوبة',
        optional: 'اختيارية',
        addPhoto: 'أضف صورة',
        retake: 'استبدل',
        progress: 'تم',
        ofRequired: 'من المطلوبة',
        back: 'رجوع',
        step3: 'الخطوة 3 · الرقم التسلسلي',
        step3Heading: 'رمز الأصالة (اختياري)',
        step3Sub: 'أدخل الرقم إن كان ظاهرًا — سنتحقق من تنسيقه قبل فحص الصور.',
        skipCode: 'تخطي',
        hintLabel: 'ملاحظة إضافية (اختياري)',
        hintPh: 'مثال: اشتُريت في باريس 2019، مستعملة حالة ممتازة',
        startScan: 'ابدأ الفحص',
        scanningTitle: 'الفحص جارٍ',
        scanHint: 'يُشغَّل النموذج على كل صورة بالتوازي — ستغرق الخطوة 20-40 ثانية.',
        resultTitle: 'نتيجة الفحص',
        overallVerdict: 'الحكم العام',
        confidence: 'نسبة الثقة',
        risk: 'مستوى المخاطرة',
        perAngleTitle: 'تفاصيل الصور',
        codeVerdictTitle: 'التحقق من الرقم',
        recommendations: 'التوصيات',
        scanAgain: 'فحص قطعة جديدة',
        errorTitle: 'لم نستطع إكمال الفحص',
        errorHint: 'تأكد من الاتصال وحاول بصور أوضح.',
        risks: { low: 'منخفضة', medium: 'متوسطة', high: 'عالية' } as const,
        verdictMap: {
          likely_authentic: 'على الأرجح أصلية',
          insufficient_evidence: 'أدلة غير كافية',
          inconclusive: 'غير حاسم',
          likely_counterfeit: 'على الأرجح مقلَّدة',
        },
        codeFormatMap: {
          valid: 'تنسيق متوافق',
          suspicious_format: 'تنسيق مشبوه',
          unknown_format: 'تنسيق غير معروف',
        },
        statusMap: { pass: 'مطابقة', caution: 'تحذير', fail: 'فشل' },
      }
    : {
        ribbon: 'Pro',
        eyebrow: 'AI Authenticity scanner',
        heading: "An expert's eye, in your pocket.",
        sub: 'Multi-angle analysis trained on brand-specific authentication protocols. Six shots, Claude Opus Vision, an aggregated verdict — no image is stored.',
        step1: 'Step 1 · Brand',
        step1Heading: 'What are we looking at?',
        step1Sub: 'We tailor the scan to the brand\'s real-world authentication protocol.',
        itemTypeLabel: 'Item type',
        continue: 'Continue',
        step2: 'Step 2 · Photos',
        step2Heading: 'Capture the required angles',
        step2Sub: 'Each angle reveals something the others miss. Required ones are highlighted in gold.',
        required: 'required',
        optional: 'optional',
        addPhoto: 'Add photo',
        retake: 'Retake',
        progress: 'Captured',
        ofRequired: 'of required',
        back: 'Back',
        step3: 'Step 3 · Code',
        step3Heading: 'Authenticity code (optional)',
        step3Sub: 'Enter the serial / date code if visible — we\'ll validate its format before running the photo check.',
        skipCode: 'Skip',
        hintLabel: 'Optional context',
        hintPh: 'e.g. Bought in Paris 2019, pre-loved, excellent condition',
        startScan: 'Run authenticity check',
        scanningTitle: 'Scanning',
        scanHint: 'The model runs on every photo in parallel — this takes 20-40 seconds.',
        resultTitle: 'Authenticity report',
        overallVerdict: 'Overall verdict',
        confidence: 'Confidence',
        risk: 'Risk level',
        perAngleTitle: 'What each photo showed',
        codeVerdictTitle: 'Code validation',
        recommendations: 'Recommendations',
        scanAgain: 'Scan another piece',
        errorTitle: "We couldn't finish the scan",
        errorHint: 'Check your connection and try again with clearer photos.',
        risks: { low: 'Low', medium: 'Medium', high: 'High' } as const,
        verdictMap: {
          likely_authentic: 'Likely authentic',
          insufficient_evidence: 'Insufficient evidence',
          inconclusive: 'Inconclusive',
          likely_counterfeit: 'Likely counterfeit',
        },
        codeFormatMap: {
          valid: 'Format looks valid',
          suspicious_format: 'Format looks suspicious',
          unknown_format: 'Format not registered',
        },
        statusMap: { pass: 'Pass', caution: 'Caution', fail: 'Fail' },
      }
  ), [isAr]);
}

// ------------------------------------------------------------------------
// Main component
// ------------------------------------------------------------------------

export function AuthenticityScanner() {
  const { locale } = useLocale();
  const isAr = locale === 'ar';
  const c = useCopy(isAr);

  const [step, setStep] = useState<Step>('brand');
  // v3.7 W2 — per-scan photo storage consent. Defaults match the
  // PDPL Art. 6 compliant decision: Tier-1 dispute storage default ON
  // (defensible as part of the service contract — re-examine on
  // contest), Tier-2 training default OFF (separate purpose, opt-in).
  const [photoConsent, setPhotoConsent] = useState<PhotoConsent>({
    allow_dispute_storage: true,
    allow_training_use: false,
  });
  const [brandKey, setBrandKey] = useState<BrandProtocolKey>('louis_vuitton');
  const [itemType, setItemType] = useState<string>('Handbag');
  const [photos, setPhotos] = useState<CapturedPhoto[]>([]);
  const [code, setCode] = useState('');
  const [hint, setHint] = useState('');
  const [result, setResult] = useState<AuthenticityResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [activeAngle, setActiveAngle] = useState<PhotoAngle | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // Anchor the visible top of the wizard so we can scroll the user
  // there on every step change. Without this, clicking Next swaps
  // the React subtree but the viewport stays where it was, so the
  // new step's input field can land below the fold and look as if
  // the page "jumped up".
  const wizardTopRef = useRef<HTMLDivElement>(null);

  // Scroll the wizard back into view whenever the step changes.
  // Skips the very first render so we don't hijack page-load scroll.
  const isFirstStepRender = useRef(true);
  useEffect(() => {
    if (isFirstStepRender.current) {
      isFirstStepRender.current = false;
      return;
    }
    if (typeof window === 'undefined' || !wizardTopRef.current) return;
    // smooth + small offset so the step heading is visible, not flush to top.
    const top = wizardTopRef.current.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  }, [step]);

  const protocol: UiBrandProtocol = useMemo(() => getUiProtocol(brandKey), [brandKey]);
  const requiredAngles = protocol.angles.filter((a) => a.required);
  const requiredDone = requiredAngles.filter((a) => photos.some((p) => p.angle === a.angle)).length;
  const canContinueFromPhotos = requiredDone >= requiredAngles.length;

  useEffect(() => {
    // Reset itemType if brand changes and current type isn't in the new list
    if (!protocol.itemTypes.includes(itemType)) {
      setItemType(protocol.itemTypes[0] ?? 'Handbag');
    }
  }, [protocol, itemType]);

  function reset() {
    setStep('brand');
    setPhotos([]);
    setCode('');
    setHint('');
    setResult(null);
    setErrorMsg(null);
    setActiveAngle(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  function openPickerFor(angle: PhotoAngle) {
    setActiveAngle(angle);
    setTimeout(() => fileRef.current?.click(), 0);
  }

  async function onFileChosen(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/') || activeAngle == null) return;
    try {
      const [{ image, mimeType }, meta] = await Promise.all([
        fileToDataUrl(file),
        readPhotoMeta(file),
      ]);
      setPhotos((prev) => [...prev.filter((p) => p.angle !== activeAngle), { angle: activeAngle, image, mimeType, meta }]);
    } catch {
      // silently fail; the retry affordance is in the UI
    }
    if (fileRef.current) fileRef.current.value = '';
    setActiveAngle(null);
  }

  function onDrop(angle: PhotoAngle, e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    void Promise.all([fileToDataUrl(file), readPhotoMeta(file)]).then(([{ image, mimeType }, meta]) => {
      setPhotos((prev) => [...prev.filter((p) => p.angle !== angle), { angle, image, mimeType, meta }]);
    });
  }

  async function runScan() {
    setErrorMsg(null);
    setResult(null);
    setStep('scanning');

    try {
      const res = await fetch(`${API_BASE}/api/authenticity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brand: protocol.displayName,
          itemType,
          photos: photos.map((p) => ({
            angle: p.angle,
            image: p.image.replace(/^data:image\/[a-zA-Z+]+;base64,/, ''),
            mimeType: p.mimeType,
          })),
          photoMetadata: photos.map((p) => p.meta ?? {}),
          code: code.trim() || undefined,
          userHint: hint.trim() || undefined,
          // v3.7 W2 — per-scan photo storage consent
          photoStorageConsent: {
            allow_dispute_storage: photoConsent.allow_dispute_storage,
            allow_training_use: photoConsent.allow_training_use,
            consent_version: 1,
          },
        }),
      });
      const json = (await res.json()) as { success: boolean; data?: AuthenticityResult; error?: string };
      if (!json.success || !json.data) {
        setErrorMsg(json.error ?? c.errorHint);
        setStep('error');
        return;
      }
      setResult(json.data);
      setStep('results');
    } catch {
      setErrorMsg(c.errorHint);
      setStep('error');
    }
  }

  // ----------------------------------------------------------------------

  return (
    <section className="relative py-12 sm:py-16 md:py-32 border-t border-white/5 bg-gradient-to-b from-obsidian to-obsidian-800">
      <div className="mx-auto max-w-7xl px-5 sm:px-6" dir={isAr ? 'rtl' : 'ltr'}>
        <div className="max-w-2xl mb-12">
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="text-[10px] tracking-[0.3em] uppercase text-gold-soft font-semibold">
              {c.eyebrow}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-emerald/15 text-emerald-soft text-[9px] tracking-[0.25em] uppercase font-bold border border-emerald/20">
              {c.ribbon}
            </span>
          </div>
          <TextReveal as="h2" className="font-serif text-display-sm text-cream leading-[0.98]">
            {c.heading}
          </TextReveal>
          <p className="mt-6 text-fog-300 text-lg leading-relaxed max-w-xl">{c.sub}</p>
        </div>

        {/* Hidden file input used by all "add photo" slots */}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onFileChosen}
          className="hidden"
        />

        {/* Anchor for step-change scroll-into-view */}
        <div ref={wizardTopRef} aria-hidden="true" />

        {/* Stepper ribbon */}
        <StepRibbon step={step} isAr={isAr} c={c} />

        {/* Animated step container */}
        <div className="relative mt-10 min-h-[520px]">
          <AnimatePresence mode="wait">
            {step === 'brand' && (
              <motion.div key="brand" {...stepMotion}>
                <BrandStep
                  selected={brandKey}
                  onSelect={setBrandKey}
                  itemType={itemType}
                  onItemType={setItemType}
                  protocol={protocol}
                  isAr={isAr}
                  c={c}
                  onContinue={() => setStep('photos')}
                />
              </motion.div>
            )}

            {step === 'photos' && (
              <motion.div key="photos" {...stepMotion}>
                <PhotosStep
                  protocol={protocol}
                  photos={photos}
                  onPick={openPickerFor}
                  onDrop={onDrop}
                  onRemove={(angle) => setPhotos((p) => p.filter((x) => x.angle !== angle))}
                  isAr={isAr}
                  c={c}
                  onBack={() => setStep('brand')}
                  onContinue={() => setStep('code')}
                  canContinue={canContinueFromPhotos}
                  requiredDone={requiredDone}
                  requiredTotal={requiredAngles.length}
                />
              </motion.div>
            )}

            {step === 'code' && (
              <motion.div key="code" {...stepMotion}>
                <CodeStep
                  protocol={protocol}
                  code={code}
                  onCode={setCode}
                  hint={hint}
                  onHint={setHint}
                  isAr={isAr}
                  c={c}
                  onBack={() => setStep('photos')}
                  onStart={() => setStep('consent')}
                />
              </motion.div>
            )}

            {step === 'consent' && (
              <motion.div key="consent" {...stepMotion}>
                <ConsentStep
                  consent={photoConsent}
                  onChange={setPhotoConsent}
                  isAr={isAr}
                  onBack={() => setStep('code')}
                  onStart={runScan}
                />
              </motion.div>
            )}

            {step === 'scanning' && (
              <motion.div key="scanning" {...stepMotion}>
                <ScanningStep isAr={isAr} c={c} photos={photos} />
              </motion.div>
            )}

            {step === 'results' && result && (
              <motion.div key="results" {...stepMotion}>
                <ResultsStep result={result} isAr={isAr} c={c} onAgain={reset} />
              </motion.div>
            )}

            {step === 'error' && (
              <motion.div key="error" {...stepMotion}>
                <ErrorStep msg={errorMsg ?? c.errorHint} isAr={isAr} c={c} onAgain={reset} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

// ------------------------------------------------------------------------
// Motion + utilities
// ------------------------------------------------------------------------

const stepMotion = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
  transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] },
};

// ------------------------------------------------------------------------
// Step: Brand + Item type
// ------------------------------------------------------------------------

function BrandStep({
  selected,
  onSelect,
  itemType,
  onItemType,
  protocol,
  isAr,
  c,
  onContinue,
}: {
  selected: BrandProtocolKey;
  onSelect: (k: BrandProtocolKey) => void;
  itemType: string;
  onItemType: (t: string) => void;
  protocol: UiBrandProtocol;
  isAr: boolean;
  c: ReturnType<typeof useCopy>;
  onContinue: () => void;
}) {
  return (
    <div className="rounded-luxe bg-obsidian-700/40 border border-white/5 p-6 md:p-10">
      <div className="text-[10px] tracking-[0.3em] uppercase text-gold-soft mb-3">{c.step1}</div>
      <h3 className="font-serif text-cream text-2xl md:text-3xl mb-2">{c.step1Heading}</h3>
      <p className="text-fog-400 text-sm mb-8 max-w-xl">{c.step1Sub}</p>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        {UI_BRAND_PROTOCOLS.map((p) => {
          const active = p.key === selected;
          return (
            <button
              key={p.key}
              onClick={() => onSelect(p.key)}
              className={`relative py-4 px-3 rounded-xl text-sm font-semibold transition-all duration-300 border ${
                active
                  ? 'bg-gold/15 text-gold-soft border-gold/40'
                  : 'bg-obsidian-800/60 text-fog-300 border-white/5 hover:border-white/20'
              }`}
            >
              {p.displayName}
            </button>
          );
        })}
      </div>

      <div className="mb-8">
        <label className="block text-[10px] tracking-[0.3em] uppercase text-fog-500 mb-2">
          {c.itemTypeLabel}
        </label>
        <div className="flex flex-wrap gap-2">
          {protocol.itemTypes.map((t) => {
            const active = t === itemType;
            return (
              <button
                key={t}
                onClick={() => onItemType(t)}
                className={`px-3.5 py-2 rounded-full text-xs tracking-wide transition border ${
                  active
                    ? 'bg-gold/20 text-gold-soft border-gold/40'
                    : 'bg-obsidian-800/60 text-fog-300 border-white/5 hover:border-white/20'
                }`}
              >
                {t}
              </button>
            );
          })}
        </div>
      </div>

      <button
        onClick={onContinue}
        className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-gold-deep to-gold text-obsidian-900 font-bold text-sm tracking-wide hover:shadow-glow-gold transition"
      >
        {c.continue}
        <span className={isAr ? 'rotate-180' : ''}>→</span>
      </button>
    </div>
  );
}

// ------------------------------------------------------------------------
// Step: Photos grid
// ------------------------------------------------------------------------

function PhotosStep({
  protocol,
  photos,
  onPick,
  onDrop,
  onRemove,
  isAr,
  c,
  onBack,
  onContinue,
  canContinue,
  requiredDone,
  requiredTotal,
}: {
  protocol: UiBrandProtocol;
  photos: CapturedPhoto[];
  onPick: (a: PhotoAngle) => void;
  onDrop: (a: PhotoAngle, e: DragEvent<HTMLDivElement>) => void;
  onRemove: (a: PhotoAngle) => void;
  isAr: boolean;
  c: ReturnType<typeof useCopy>;
  onBack: () => void;
  onContinue: () => void;
  canContinue: boolean;
  requiredDone: number;
  requiredTotal: number;
}) {
  return (
    <div className="rounded-luxe bg-obsidian-700/40 border border-white/5 p-6 md:p-10">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-gold-soft mb-2">{c.step2}</div>
          <h3 className="font-serif text-cream text-2xl md:text-3xl mb-1">{c.step2Heading}</h3>
          <p className="text-fog-400 text-sm max-w-xl">{c.step2Sub}</p>
        </div>
        <div className="text-right">
          <div className="text-[10px] tracking-[0.2em] uppercase text-fog-500">{c.progress}</div>
          <div className="font-mono text-cream text-2xl font-bold">
            {requiredDone}/{requiredTotal}
          </div>
          <div className="text-[10px] text-fog-500">{c.ofRequired}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {protocol.angles.map((a) => {
          const captured = photos.find((p) => p.angle === a.angle);
          return (
            <AngleSlot
              key={a.angle}
              spec={a}
              captured={captured}
              isAr={isAr}
              c={c}
              onPick={() => onPick(a.angle)}
              onDrop={(e) => onDrop(a.angle, e)}
              onRemove={() => onRemove(a.angle)}
            />
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <button
          onClick={onBack}
          className="text-sm text-fog-400 hover:text-cream tracking-wide"
        >
          ← {c.back}
        </button>
        <button
          onClick={onContinue}
          disabled={!canContinue}
          className={`inline-flex items-center gap-3 px-6 py-3 rounded-full font-bold text-sm tracking-wide transition ${
            canContinue
              ? 'bg-gradient-to-r from-gold-deep to-gold text-obsidian-900 hover:shadow-glow-gold'
              : 'bg-obsidian-800 text-fog-500 cursor-not-allowed'
          }`}
        >
          {c.continue}
          <span className={isAr ? 'rotate-180' : ''}>→</span>
        </button>
      </div>
    </div>
  );
}

function AngleSlot({
  spec,
  captured,
  isAr,
  c,
  onPick,
  onDrop,
  onRemove,
}: {
  spec: UiAngle;
  captured: CapturedPhoto | undefined;
  isAr: boolean;
  c: ReturnType<typeof useCopy>;
  onPick: () => void;
  onDrop: (e: DragEvent<HTMLDivElement>) => void;
  onRemove: () => void;
}) {
  const label = isAr ? spec.label.ar : spec.label.en;
  const instr = isAr ? spec.instruction.ar : spec.instruction.en;
  const [dragOver, setDragOver] = useState(false);

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        setDragOver(false);
        onDrop(e);
      }}
      className={`relative rounded-2xl border overflow-hidden transition-all ${
        captured
          ? 'border-emerald/30 bg-emerald/5'
          : spec.required
          ? dragOver
            ? 'border-gold bg-gold/10'
            : 'border-gold/30 bg-obsidian-800/40'
          : 'border-white/5 bg-obsidian-800/20'
      }`}
    >
      {captured ? (
        <>
          <img src={captured.image} alt={label} className="w-full aspect-[4/3] object-cover" />
          <div className="absolute inset-x-0 top-0 p-3 bg-gradient-to-b from-obsidian/80 to-transparent flex items-start justify-between">
            <div>
              <div className="text-[9px] tracking-[0.25em] uppercase text-cream font-bold">{label}</div>
              <div className="text-[9px] tracking-wide text-emerald-soft">✓ {spec.required ? c.required : c.optional}</div>
            </div>
            <button
              onClick={onRemove}
              className="rounded-full w-6 h-6 bg-obsidian-900/80 text-cream text-xs grid place-items-center hover:bg-rose/50 transition"
              aria-label={c.retake}
            >
              ✕
            </button>
          </div>
          <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-obsidian/90 to-transparent">
            <button
              onClick={onPick}
              className="text-[10px] text-gold-soft tracking-widest uppercase font-bold hover:text-gold transition"
            >
              {c.retake}
            </button>
          </div>
        </>
      ) : (
        <button onClick={onPick} className="block w-full text-start p-5 aspect-[4/3] flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[10px] tracking-[0.25em] uppercase font-bold ${spec.required ? 'text-gold-soft' : 'text-fog-500'}`}>
                {spec.required ? c.required : c.optional}
              </span>
            </div>
            <div className="font-serif text-cream text-lg mb-2">{label}</div>
            <p className="text-xs text-fog-400 leading-relaxed">{instr}</p>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <div className="rounded-full w-10 h-10 bg-obsidian-900 border border-gold/20 grid place-items-center text-gold-soft">
              <CameraIcon size={18} />
            </div>
            <span className="text-[10px] tracking-widest uppercase text-fog-300 font-bold">{c.addPhoto}</span>
          </div>
        </button>
      )}
    </div>
  );
}

// ------------------------------------------------------------------------
// Step: Code + Hint
// ------------------------------------------------------------------------

function CodeStep({
  protocol,
  code,
  onCode,
  hint,
  onHint,
  isAr,
  c,
  onBack,
  onStart,
}: {
  protocol: UiBrandProtocol;
  code: string;
  onCode: (s: string) => void;
  hint: string;
  onHint: (s: string) => void;
  isAr: boolean;
  c: ReturnType<typeof useCopy>;
  onBack: () => void;
  onStart: () => void;
}) {
  return (
    <div className="rounded-luxe bg-obsidian-700/40 border border-white/5 p-6 md:p-10">
      <div className="text-[10px] tracking-[0.3em] uppercase text-gold-soft mb-3">{c.step3}</div>
      <h3 className="font-serif text-cream text-2xl md:text-3xl mb-2">{c.step3Heading}</h3>
      <p className="text-fog-400 text-sm mb-8 max-w-xl">{c.step3Sub}</p>

      {protocol.codeFormat && (
        <div className="mb-6 max-w-xl">
          <label className="block text-[10px] tracking-[0.2em] uppercase text-fog-500 mb-2">
            {isAr ? protocol.codeFormat.label.ar : protocol.codeFormat.label.en}
          </label>
          <input
            type="text"
            value={code}
            onChange={(e) => onCode(e.target.value)}
            placeholder={protocol.codeFormat.example}
            className="w-full rounded-lg bg-obsidian-900/80 border border-white/10 text-cream px-4 py-3 text-sm font-mono tracking-wider focus:outline-none focus:border-gold/60"
          />
          <p className="mt-2 text-xs text-fog-500">
            {isAr ? protocol.codeFormat.note.ar : protocol.codeFormat.note.en}
          </p>
        </div>
      )}

      <div className="mb-10 max-w-xl">
        <label className="block text-[10px] tracking-[0.2em] uppercase text-fog-500 mb-2">{c.hintLabel}</label>
        <textarea
          rows={2}
          value={hint}
          onChange={(e) => onHint(e.target.value)}
          placeholder={c.hintPh}
          className="w-full rounded-lg bg-obsidian-900/80 border border-white/10 text-cream px-4 py-3 text-sm resize-none focus:outline-none focus:border-gold/60"
        />
      </div>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <button onClick={onBack} className="text-sm text-fog-400 hover:text-cream tracking-wide">
          ← {c.back}
        </button>
        <button
          onClick={onStart}
          className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-gold-deep to-gold text-obsidian-900 font-bold text-sm tracking-wide hover:shadow-glow-gold transition"
        >
          {c.startScan}
          <span className={isAr ? 'rotate-180' : ''}>→</span>
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------
// Step: Photo storage consent (PDPL Art. 6 — separate purpose, separate
// checkbox; Tier-1 default ON, Tier-2 default OFF). Last step before
// the LLM call so the user has a chance to set their preference per scan.
// ------------------------------------------------------------------------

function ConsentStep({
  consent,
  onChange,
  isAr,
  onBack,
  onStart,
}: {
  consent: PhotoConsent;
  onChange: (c: PhotoConsent) => void;
  isAr: boolean;
  onBack: () => void;
  onStart: () => void;
}) {
  const copy = isAr
    ? {
        title: 'كيف نتعامل مع صورك',
        sub: 'يمكنك تغيير هذه الخيارات في أي وقت من إعدادات الخصوصية.',
        tier1Title: 'احتفظ بالصور لمدة ٩٠ يومًا',
        tier1Body: 'نخزن نسخة من صورك لمدة ٩٠ يومًا فقط لمراجعة النتيجة معك إذا اعترضت عليها أو احتجت إلى تصعيد لمحقق شريك. ثم تُحذف تلقائيًا.',
        tier2Title: 'ساعدنا في تحسين الذكاء الاصطناعي',
        tier2Body: 'نستخدم صورك كبيانات تدريب لتحسين دقة المصادقة لجميع المستخدمين. اختياري تمامًا. يمكنك سحب الموافقة في أي وقت من /me/data.',
        tier2Tag: 'اختياري',
        back: 'رجوع',
        startWithStorage: 'ابدأ المصادقة',
        startNoStorage: 'ابدأ المصادقة (بدون حفظ الصور)',
        compliance: 'متوافق مع قانون حماية البيانات الإماراتي ٢٠٢١.',
      }
    : {
        title: 'How we handle your photos',
        sub: 'You can change these any time in your privacy settings.',
        tier1Title: 'Keep my photos for 90 days',
        tier1Body: "We'll store your photos for 90 days, only to re-examine them with you if you contest the verdict, or to share them with a partner expert if you escalate. After 90 days they're automatically deleted.",
        tier2Title: 'Help us improve the AI',
        tier2Body: 'Use my photos as training data to improve authentication accuracy for everyone. Fully optional. You can withdraw consent any time at /me/data.',
        tier2Tag: 'Optional',
        back: 'Back',
        startWithStorage: 'Start authentication',
        startNoStorage: 'Start authentication (no photos saved)',
        compliance: "PDPL-compliant. We'll never store photos without your explicit consent.",
      };
  const anyStorage = consent.allow_dispute_storage || consent.allow_training_use;
  return (
    <div className="rounded-luxe bg-gradient-to-br from-obsidian-700 via-obsidian-700 to-obsidian-800 border border-white/5 p-6 sm:p-8">
      <div className="text-[10px] tracking-[0.3em] uppercase text-gold-soft font-semibold mb-3">
        {isAr ? 'الخصوصية' : 'Privacy'}
      </div>
      <h3 className="font-serif text-cream text-2xl sm:text-3xl mb-2">{copy.title}</h3>
      <p className="text-fog-400 text-sm mb-6">{copy.sub}</p>

      <div className="space-y-4">
        {/* Tier 1: dispute storage — default ON */}
        <label className="block cursor-pointer rounded-xl bg-obsidian-800/60 border border-white/10 p-5 hover:border-gold/30 transition">
          <div className="flex items-start gap-4">
            <input
              type="checkbox"
              checked={consent.allow_dispute_storage}
              onChange={(e) => onChange({ ...consent, allow_dispute_storage: e.target.checked })}
              className="mt-1 w-5 h-5 accent-gold-deep cursor-pointer"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-cream text-sm font-bold">{copy.tier1Title}</span>
              </div>
              <p className="text-fog-400 text-xs leading-relaxed">{copy.tier1Body}</p>
            </div>
          </div>
        </label>

        {/* Tier 2: training — default OFF, opt-in */}
        <label className="block cursor-pointer rounded-xl bg-obsidian-800/60 border border-white/10 p-5 hover:border-gold/30 transition">
          <div className="flex items-start gap-4">
            <input
              type="checkbox"
              checked={consent.allow_training_use}
              onChange={(e) => onChange({ ...consent, allow_training_use: e.target.checked })}
              className="mt-1 w-5 h-5 accent-gold-deep cursor-pointer"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-cream text-sm font-bold">{copy.tier2Title}</span>
                <span className="text-[9px] tracking-[0.2em] uppercase text-gold-soft font-bold px-2 py-0.5 rounded-full bg-gold/10 border border-gold/20">
                  {copy.tier2Tag}
                </span>
              </div>
              <p className="text-fog-400 text-xs leading-relaxed">{copy.tier2Body}</p>
            </div>
          </div>
        </label>
      </div>

      <p className="mt-6 text-fog-500 text-[10px] leading-relaxed">{copy.compliance}</p>

      <div className="mt-8 flex items-center justify-between">
        <button
          onClick={onBack}
          className="text-xs tracking-[0.25em] uppercase text-fog-400 hover:text-cream transition"
        >
          {isAr ? '←' : '←'} {copy.back}
        </button>
        <button
          onClick={onStart}
          className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-gold-deep to-gold text-obsidian-900 font-bold text-sm tracking-wide hover:shadow-glow-gold transition"
        >
          {anyStorage ? copy.startWithStorage : copy.startNoStorage}
          <span className={isAr ? 'rotate-180' : ''}>→</span>
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------
// Step: Scanning state (streams per-angle progress indicators)
// ------------------------------------------------------------------------

function ScanningStep({
  isAr,
  c,
  photos,
}: {
  isAr: boolean;
  c: ReturnType<typeof useCopy>;
  photos: CapturedPhoto[];
}) {
  return (
    <div className="rounded-luxe bg-obsidian-700 border border-gold/20 p-8 md:p-12 flex flex-col">
      <div className="flex items-center gap-4 mb-8" dir={isAr ? 'rtl' : 'ltr'}>
        <div className="w-10 h-10 rounded-full border-2 border-gold/30 border-t-gold animate-spin" />
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-gold-soft font-bold mb-1">{c.scanningTitle}</div>
          <div className="text-fog-400 text-sm">{c.scanHint}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {photos.map((p, i) => (
          <motion.div
            key={p.angle}
            initial={{ opacity: 0.3 }}
            animate={{ opacity: 1 }}
            transition={{ delay: i * 0.25, repeat: Infinity, repeatType: 'reverse', duration: 0.9 }}
            className="relative rounded-xl overflow-hidden border border-white/5"
          >
            <img src={p.image} alt={p.angle} className="w-full aspect-square object-cover saturate-[0.6]" />
            <motion.div
              initial={{ top: '-8%' }}
              animate={{ top: '108%' }}
              transition={{ duration: 1.6, repeat: Infinity, ease: 'linear', delay: i * 0.2 }}
              className="absolute inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-gold to-transparent"
            />
            <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-obsidian/90 to-transparent">
              <div className="text-[9px] tracking-widest uppercase text-cream">{p.angle.replace(/_/g, ' ')}</div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------
// Step: Results
// ------------------------------------------------------------------------

function ResultsStep({
  result,
  isAr,
  c,
  onAgain,
}: {
  result: AuthenticityResult;
  isAr: boolean;
  c: ReturnType<typeof useCopy>;
  onAgain: () => void;
}) {
  const tone =
    result.verdict === 'likely_authentic'
      ? { accent: 'text-emerald-soft', bg: 'bg-emerald/10', border: 'border-emerald/20', ring: 'ring-emerald/30', icon: '✓' }
      : result.verdict === 'likely_counterfeit'
      ? { accent: 'text-rose', bg: 'bg-rose/10', border: 'border-rose/20', ring: 'ring-rose/30', icon: '✕' }
      : { accent: 'text-gold-soft', bg: 'bg-gold/10', border: 'border-gold/20', ring: 'ring-gold/30', icon: '?' };

  return (
    <div className="rounded-luxe bg-cream text-obsidian-900 p-6 md:p-10 shadow-card-lg" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="flex items-start justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-gold-deep font-bold mb-1">{c.resultTitle}</div>
          <div className={`font-serif text-3xl md:text-4xl font-bold inline-flex items-center gap-3 ${tone.accent}`}>
            <span className={`w-10 h-10 rounded-full grid place-items-center text-2xl ${tone.bg} ${tone.border} border`}>
              {tone.icon}
            </span>
            {c.verdictMap[result.verdict]}
          </div>
          <p className="mt-3 text-sm text-obsidian-800 max-w-xl leading-relaxed">{result.summary}</p>
        </div>
        <div className="flex items-center gap-3">
          <Metric label={c.confidence} value={`${result.confidence}%`} />
          <Metric
            label={c.risk}
            value={c.risks[result.riskLevel]}
            tone={result.riskLevel === 'high' ? 'rose' : result.riskLevel === 'medium' ? 'gold' : 'emerald'}
          />
        </div>
      </div>

      {/* Confidence bar */}
      <div className="mb-8">
        <div className="h-2 rounded-full bg-obsidian-900/10 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${result.confidence}%` }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
            className="h-full bg-gradient-to-r from-gold-deep to-gold rounded-full"
          />
        </div>
      </div>

      {/* Code validation */}
      {result.codeValidation && (
        <div className="mb-8 rounded-xl bg-obsidian-900/5 p-4 border border-obsidian-900/10">
          <div className="text-[10px] tracking-[0.2em] uppercase text-gold-deep font-bold mb-2">{c.codeVerdictTitle}</div>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="font-mono text-lg font-bold text-obsidian-900">{result.codeValidation.provided}</div>
            <div
              className={`text-xs font-bold uppercase tracking-widest ${
                result.codeValidation.format === 'valid'
                  ? 'text-emerald'
                  : result.codeValidation.format === 'suspicious_format'
                  ? 'text-rose'
                  : 'text-fog-700'
              }`}
            >
              {c.codeFormatMap[result.codeValidation.format]}
              {result.codeValidation.yearInferred ? ` · ${result.codeValidation.yearInferred}` : ''}
            </div>
          </div>
          <p className="text-xs text-fog-700 mt-1">{result.codeValidation.reason}</p>
        </div>
      )}

      {/* v3.5 — Decision-tier breakdown. Replaces the binary "verdict + %"
          experience with an explicit count of what the model actually saw.
          Skip when there's no evidence to show (insufficient_evidence on a
          fresh scan). */}
      {result.decisionBreakdown && result.decisionBreakdown.leaning !== 'insufficient' && (
        <DecisionBreakdownCard
          breakdown={result.decisionBreakdown}
          isAr={isAr}
        />
      )}

      {/* Per-angle findings grid */}
      {result.perAngle.length > 0 && (
        <div className="mb-8">
          <div className="text-[10px] tracking-[0.2em] uppercase text-gold-deep font-bold mb-3">{c.perAngleTitle}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {result.perAngle.map((p, i) => {
              const statusTone =
                p.status === 'pass' ? 'text-emerald' : p.status === 'fail' ? 'text-rose' : 'text-gold-deep';
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i }}
                  className="rounded-xl bg-obsidian-900/5 border border-obsidian-900/10 p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold uppercase tracking-widest text-obsidian-900">
                      {p.angle.replace(/_/g, ' ')}
                    </div>
                    <div className={`text-xs font-bold uppercase tracking-wider ${statusTone}`}>
                      {p.status === 'pass' ? '✓' : p.status === 'fail' ? '✕' : '!'} {c.statusMap[p.status]}
                    </div>
                  </div>
                  <p className="text-xs text-fog-700 leading-relaxed">{p.observation}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {result.recommendations.length > 0 && (
        <div className="mb-6 rounded-xl bg-gold/10 border border-gold/20 p-4">
          <div className="text-[10px] tracking-[0.2em] uppercase text-gold-deep font-bold mb-2">{c.recommendations}</div>
          <ul className="list-disc ps-5 space-y-1 text-sm text-obsidian-800">
            {result.recommendations.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Track in portfolio — fusion between auth verdict and portfolio.
          Shows on confident "authentic" verdicts so the user can flow
          directly from "yes, it's real" into tracking resale value. */}
      {result.verdict === 'likely_authentic' && result.confidence >= 55 && (
        <AddToPortfolioCard protocol={result.protocol} isAr={isAr} />
      )}

      {/* Pro-authentication escalation — only when AI is uncertain or flagging.
          v3.6: pass result.escalation + result.scanId so the card can render
          a real "Submit for partner review" button when the API marked the
          scan as eligible for the HITL pipeline. */}
      {(result.verdict !== 'likely_authentic' || result.confidence < 65) && (
        <HumanEscalationCard
          brand={result.protocol}
          isAr={isAr}
          escalation={result.escalation}
          scanId={result.scanId ?? null}
        />
      )}

      {/* Feedback widget — only shows when we have a scanId to attach to */}
      {result.scanId && <FeedbackWidget scanId={result.scanId} isAr={isAr} />}

      <button
        onClick={onAgain}
        className="text-xs tracking-[0.25em] uppercase text-gold-deep font-bold hover:text-gold transition"
      >
        ↻ {c.scanAgain}
      </button>
    </div>
  );
}

/**
 * Surface a "get professional certainty" card when our confidence is
 * low or the verdict leans counterfeit. Links to services we trust —
 * no affiliate code at MVP, just earnest recommendations. When we
 * formalize API partnerships with Entrupy / Real Authentication, this
 * is where the deep-link flow goes.
 */
/**
 * After a confident "likely_authentic" verdict, invite the user to
 * add this piece to their cross-device portfolio. One email, auto-sync.
 * This is the authenticity → portfolio fusion the incumbents don't have.
 */
function AddToPortfolioCard({ protocol, isAr }: { protocol: string; isAr: boolean }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === 'saving') return;
    const e2 = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e2)) {
      setStatus('error');
      return;
    }
    setStatus('saving');
    try {
      const getRes = await fetch(`${API_BASE}/api/portfolio/${encodeURIComponent(e2)}`);
      const getJson = (await getRes.json()) as {
        success: boolean;
        data?: { pieces?: Array<Record<string, unknown>> };
      };
      const existing = getJson?.data?.pieces ?? [];
      const brandName = protocol.replace(/_/g, ' ');
      const sku =
        `${brandName}-scan-${Date.now().toString(36)}`.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 80);
      const merged = [
        ...existing,
        {
          sku,
          brand: brandName,
          model: 'Authenticity-verified piece',
          retailPrice: 0,
          currency: 'AED',
          addedAt: new Date().toISOString(),
        },
      ];
      const res = await fetch(`${API_BASE}/api/portfolio/${encodeURIComponent(e2)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pieces: merged, locale: isAr ? 'ar' : 'en' }),
      });
      if (!res.ok) {
        setStatus('error');
        return;
      }
      setStatus('saved');
    } catch {
      setStatus('error');
    }
  }

  const copy = isAr
    ? {
        title: 'أضفها إلى محفظتك',
        body: 'نتيجة الأصالة مطمئنة. احفظ القطعة في محفظتك لتتبع قيمتها مع الوقت.',
        placeholder: 'your@email.com',
        button: 'احفظ القطعة',
        saving: 'جارٍ الحفظ…',
        saved: 'تمت الإضافة — افتح قسم المحفظة لرؤيتها بجانب قطعك الأخرى.',
        error: 'لم نستطع الحفظ. جرّب بريدًا آخر.',
      }
    : {
        title: 'Add this to your portfolio',
        body: "The verdict looks clean. Save it to your portfolio to track its resale value over time.",
        placeholder: 'your@email.com',
        button: 'Save to portfolio',
        saving: 'Saving…',
        saved: 'Added — open the Portfolio section to see it alongside your other pieces.',
        error: "Couldn't save. Try another email.",
      };

  if (status === 'saved') {
    return (
      <div className="mb-6 rounded-xl bg-emerald/10 border border-emerald/20 p-4 text-center">
        <span className="text-emerald text-lg mr-2">✓</span>
        <span className="text-sm text-obsidian-800">{copy.saved}</span>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mb-6 rounded-xl bg-gold/10 border border-gold/20 p-4">
      <div className="text-[10px] tracking-[0.2em] uppercase text-gold-deep font-bold mb-1">
        📊 {copy.title}
      </div>
      <p className="text-xs text-fog-700 mb-3">{copy.body}</p>
      <div className="flex gap-2" dir="ltr">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={copy.placeholder}
          required
          className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-white text-obsidian-900 text-sm placeholder:text-fog-500 border border-obsidian-900/10 focus:border-gold/60 focus:outline-none"
        />
        <button
          type="submit"
          disabled={status === 'saving'}
          className="px-4 py-2 rounded-lg bg-obsidian-900 text-cream text-xs font-bold whitespace-nowrap hover:bg-obsidian-700 transition disabled:opacity-60"
        >
          {status === 'saving' ? copy.saving : copy.button}
        </button>
      </div>
      {status === 'error' && (
        <div className="mt-2 text-xs text-rose">{copy.error}</div>
      )}
    </form>
  );
}

// v3.5 — Decision-tier breakdown card. Shows the user the explicit
// count of markers / concerns / inconclusive aspects the model surfaced,
// plus the top-3 of each by weight. Replaces the binary "verdict + %"
// experience: instead of "likely_authentic @ 78%" the user sees
// "5 markers consistent with authentic, 2 concerns, 1 inconclusive —
// leaning authentic" plus the actual evidence.
function DecisionBreakdownCard({
  breakdown,
  isAr,
}: {
  breakdown: NonNullable<AuthenticityResult['decisionBreakdown']>;
  isAr: boolean;
}) {
  const copy = isAr
    ? {
        title: 'تحليل الأدلة',
        markers: 'علامات أصالة',
        concerns: 'مخاوف',
        inconclusive: 'غير حاسم',
        leaningMap: {
          strongly_authentic: 'مرجّح بقوة أنها أصلية',
          leaning_authentic: 'تميل إلى الأصالة',
          mixed: 'إشارات مختلطة',
          leaning_counterfeit: 'تميل إلى التزوير',
          strongly_counterfeit: 'مرجّح بقوة أنها مزيفة',
          insufficient: 'أدلة غير كافية',
        } as Record<string, string>,
        weight: 'الوزن',
        topMarkers: 'أبرز العلامات',
        topConcerns: 'أبرز المخاوف',
      }
    : {
        title: 'Evidence breakdown',
        markers: 'authentic markers',
        concerns: 'concerns',
        inconclusive: 'inconclusive',
        leaningMap: {
          strongly_authentic: 'Strongly leans authentic',
          leaning_authentic: 'Leans authentic',
          mixed: 'Mixed signals',
          leaning_counterfeit: 'Leans counterfeit',
          strongly_counterfeit: 'Strongly leans counterfeit',
          insufficient: 'Insufficient evidence',
        } as Record<string, string>,
        weight: 'weight',
        topMarkers: 'Top markers',
        topConcerns: 'Top concerns',
      };
  const tone =
    breakdown.leaning === 'strongly_authentic' || breakdown.leaning === 'leaning_authentic'
      ? 'text-emerald'
      : breakdown.leaning === 'strongly_counterfeit' || breakdown.leaning === 'leaning_counterfeit'
      ? 'text-rose'
      : 'text-gold-deep';
  return (
    <div className="mb-6 rounded-xl bg-cream/60 border border-obsidian-900/10 p-4">
      <div className="text-[10px] tracking-[0.2em] uppercase text-gold-deep font-bold mb-3">
        {copy.title}
      </div>
      {/* Three counters + leaning */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center p-2 rounded-lg bg-emerald/10 border border-emerald/20">
          <div className="text-2xl font-serif text-emerald font-bold">{breakdown.markersTotal}</div>
          <div className="text-[10px] uppercase tracking-wide text-fog-700 mt-1">{copy.markers}</div>
        </div>
        <div className="text-center p-2 rounded-lg bg-rose/10 border border-rose/20">
          <div className="text-2xl font-serif text-rose font-bold">{breakdown.concernsTotal}</div>
          <div className="text-[10px] uppercase tracking-wide text-fog-700 mt-1">{copy.concerns}</div>
        </div>
        <div className="text-center p-2 rounded-lg bg-fog-200/40 border border-fog-300/30">
          <div className="text-2xl font-serif text-fog-700 font-bold">{breakdown.inconclusiveTotal}</div>
          <div className="text-[10px] uppercase tracking-wide text-fog-700 mt-1">{copy.inconclusive}</div>
        </div>
      </div>
      <div className={`text-center text-sm font-bold uppercase tracking-wider mb-4 ${tone}`}>
        {copy.leaningMap[breakdown.leaning]}
      </div>
      {/* Top markers + concerns side by side */}
      {(breakdown.topMarkers.length > 0 || breakdown.topConcerns.length > 0) && (
        <div className="grid sm:grid-cols-2 gap-3">
          {breakdown.topMarkers.length > 0 && (
            <div className="rounded-lg bg-emerald/5 border border-emerald/15 p-3">
              <div className="text-[10px] uppercase tracking-wide text-emerald font-bold mb-2">
                {copy.topMarkers}
              </div>
              <ul className="space-y-1.5">
                {breakdown.topMarkers.map((m, i) => (
                  <li key={i} className="text-[11px] leading-snug text-obsidian-800 flex items-start gap-2">
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-emerald/20 text-emerald text-[10px] font-bold">
                      {m.weight}
                    </span>
                    <span>{m.note}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {breakdown.topConcerns.length > 0 && (
            <div className="rounded-lg bg-rose/5 border border-rose/15 p-3">
              <div className="text-[10px] uppercase tracking-wide text-rose font-bold mb-2">
                {copy.topConcerns}
              </div>
              <ul className="space-y-1.5">
                {breakdown.topConcerns.map((c, i) => (
                  <li key={i} className="text-[11px] leading-snug text-obsidian-800 flex items-start gap-2">
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-rose/20 text-rose text-[10px] font-bold">
                      {c.weight}
                    </span>
                    <span>{c.note}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// v3.6 — replaces the v3.4 placeholder with an actual two-path recovery
// surface. Path A is always the retake guidance; Path B becomes a LIVE
// "Submit for partner review" button when result.escalation.eligible is
// true AND we have a scanId to attach the submission to. Otherwise we
// keep the "coming soon" placeholder for ineligible scans (low-value
// brands or already-confident verdicts).
function HumanEscalationCard({
  brand,
  isAr,
  escalation,
  scanId,
}: {
  brand: string;
  isAr: boolean;
  escalation?: AuthenticityResult['escalation'];
  scanId?: string | null;
}) {
  void brand; // brand-specific logic removed — see Phase 3 of authenticator roadmap

  const copy = isAr
    ? {
        title: 'حسّن دقة النتيجة',
        retakeTitle: 'أعد تصوير اللقطات المقربة',
        retakeBody: 'أغلب الحالات غير الحاسمة تتحول إلى نتيجة واضحة عند تحسين الإضاءة وتقريب الكاميرا من الشعار والخياطة والمعدن.',
        escalateTitle: 'تصعيد إلى خبير شريك',
        escalateBody: 'للقطع عالية القيمة سنحوّل المسح إلى خبير بشري داخل صوفيا. الإجابة خلال ٢٤ ساعة. مجاني خلال مرحلة البيتا.',
        escalateBodyComingSoon: 'هذه الميزة متاحة للعلامات عالية القيمة (شانيل / هيرمس / لوي فيتون / رولكس).',
        soon: 'قريبًا',
        submitBtn: 'أرسل للمراجعة',
        emailLabel: 'بريدك الإلكتروني (اختياري)',
        notesLabel: 'ملاحظات (اختياري)',
        submitting: 'جاري الإرسال…',
        submittedTitle: 'تم إرسال المسح للمراجعة',
        submittedBody: 'سيتولى أحد الخبراء الشركاء مراجعة هذا المسح. ستصلك النتيجة على البريد الإلكتروني عند الانتهاء.',
        submittedQueued: 'في الانتظار',
        submittedReview: 'قيد المراجعة',
        submittedDone: 'اكتمل',
      }
    : {
        title: 'Tighten this verdict',
        retakeTitle: 'Retake the macro shots',
        retakeBody: 'Most uncertain verdicts in our system flip when we get sharper close-ups of the logo, stitching, hardware and any serial label under brighter natural light.',
        escalateTitle: 'Escalate to a partner expert',
        escalateBody: 'For high-value pieces we route this scan to a human authenticator inside Sophia. Answer in 24 hours. Free during the beta.',
        escalateBodyComingSoon: 'This option is reserved for high-value brands (Chanel / Hermès / Louis Vuitton / Rolex).',
        soon: 'Coming soon',
        submitBtn: 'Submit for review',
        emailLabel: 'Your email (optional, for the result)',
        notesLabel: 'Notes (optional)',
        submitting: 'Submitting…',
        submittedTitle: 'Submitted for partner review',
        submittedBody: 'A partner expert will review this scan. We will email you when the verdict lands.',
        submittedQueued: 'Queued',
        submittedReview: 'In review',
        submittedDone: 'Completed',
      };

  const eligible = Boolean(escalation?.eligible && scanId);

  // ---- Local submission state ----
  type SubmitState =
    | { kind: 'idle' }
    | { kind: 'submitting' }
    | { kind: 'submitted'; status: 'queued' | 'in_review' | 'completed'; partnerVerdict?: string | null; partnerNotes?: string | null }
    | { kind: 'error'; reason: string };
  const [state, setState] = useState<SubmitState>({ kind: 'idle' });
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  // v3.7 W3.5 — Turnstile token (null while widget hasn't yet produced
  // one; null is permitted by the API in dev when FEATURE_TURNSTILE=0).
  const [cfToken, setCfToken] = useState<string | null>(null);

  async function submit(): Promise<void> {
    if (!scanId) return;
    setState({ kind: 'submitting' });
    try {
      const res = await fetch(`${API_BASE}/api/escalations/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scanId,
          userEmail: email.trim() || null,
          userNotes: notes.trim() || null,
          cfToken,
        }),
      });
      const json = (await res.json()) as { success: boolean; escalation?: { status: 'queued' | 'in_review' | 'completed'; partnerVerdict?: string | null; partnerNotes?: string | null }; error?: string };
      if (!res.ok || !json.success) {
        setState({ kind: 'error', reason: json.error ?? `HTTP ${res.status}` });
        return;
      }
      setState({
        kind: 'submitted',
        status: json.escalation?.status ?? 'queued',
        partnerVerdict: json.escalation?.partnerVerdict ?? null,
        partnerNotes: json.escalation?.partnerNotes ?? null,
      });
    } catch (e) {
      setState({ kind: 'error', reason: (e as Error).message });
    }
  }

  return (
    <div className="mb-6 rounded-xl bg-obsidian-900/5 border border-obsidian-900/10 p-4">
      <div className="text-[10px] tracking-[0.2em] uppercase text-gold-deep font-bold mb-3">
        {copy.title}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-lg bg-cream/40 border border-obsidian-900/5 p-3">
          <div className="text-xs font-bold text-obsidian-900 mb-1">{copy.retakeTitle}</div>
          <p className="text-[11px] leading-relaxed text-fog-700">{copy.retakeBody}</p>
        </div>
        <div className="rounded-lg bg-cream/40 border border-obsidian-900/5 p-3 relative">
          <div className="text-xs font-bold text-obsidian-900 mb-1">{copy.escalateTitle}</div>

          {!eligible && (
            <>
              <span className="absolute top-2 end-2 text-[9px] tracking-[0.15em] uppercase text-gold-deep font-bold">
                {copy.soon}
              </span>
              <p className="text-[11px] leading-relaxed text-fog-700">{copy.escalateBodyComingSoon}</p>
            </>
          )}

          {eligible && state.kind === 'idle' && (
            <>
              <p className="text-[11px] leading-relaxed text-fog-700 mb-3">{copy.escalateBody}</p>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={copy.emailLabel}
                className="w-full mb-2 px-3 py-1.5 rounded-md bg-white border border-obsidian-900/10 text-[11px] text-obsidian-900 placeholder:text-fog-500 focus:border-gold-deep/40 focus:outline-none"
              />
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={copy.notesLabel}
                rows={2}
                maxLength={2000}
                className="w-full mb-2 px-3 py-1.5 rounded-md bg-white border border-obsidian-900/10 text-[11px] text-obsidian-900 placeholder:text-fog-500 focus:border-gold-deep/40 focus:outline-none resize-none"
              />
              <TurnstileWidget action="escalation-submit" onToken={setCfToken} />
              <button
                onClick={() => void submit()}
                className="w-full px-3 py-1.5 rounded-pill bg-obsidian-900 text-cream text-[11px] font-bold uppercase tracking-wider hover:bg-obsidian-700 transition"
              >
                {copy.submitBtn}
              </button>
            </>
          )}

          {state.kind === 'submitting' && (
            <p className="text-[11px] text-fog-700 italic">{copy.submitting}</p>
          )}

          {state.kind === 'error' && (
            <p className="text-[11px] text-rose mt-1">{state.reason}</p>
          )}

          {state.kind === 'submitted' && (
            <div>
              <p className="text-[11px] leading-relaxed text-emerald font-semibold mb-1">{copy.submittedTitle}</p>
              <p className="text-[11px] leading-relaxed text-fog-700 mb-2">{copy.submittedBody}</p>
              <div className="text-[10px] tracking-[0.15em] uppercase text-gold-deep font-bold">
                {state.status === 'queued' ? copy.submittedQueued : state.status === 'in_review' ? copy.submittedReview : copy.submittedDone}
              </div>
              {state.partnerVerdict && (
                <div className="mt-2 rounded-md bg-white p-2 border border-obsidian-900/5">
                  <div className="text-[10px] uppercase tracking-wider text-fog-500">Verdict</div>
                  <div className="text-xs font-bold text-obsidian-900">{state.partnerVerdict}</div>
                  {state.partnerNotes && <p className="text-[11px] text-fog-700 mt-1 whitespace-pre-wrap">{state.partnerNotes}</p>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Post-verdict feedback widget. Submits { scanId, rating, notes? } to
 * /api/authenticity/feedback. Fire-and-forget UI — once the user votes,
 * we thank them and lock the control so it can't be double-submitted.
 */
function FeedbackWidget({ scanId, isAr }: { scanId: string; isAr: boolean }) {
  const [submitted, setSubmitted] = useState<'helpful' | 'not_helpful' | 'flagged_wrong' | null>(null);
  const [sending, setSending] = useState(false);

  async function send(rating: 'helpful' | 'not_helpful' | 'flagged_wrong') {
    if (submitted || sending) return;
    setSending(true);
    try {
      await fetch(`${API_BASE}/api/authenticity/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scanId, rating }),
      });
      setSubmitted(rating);
    } catch {
      // silent — don't block the user; they got their verdict
    } finally {
      setSending(false);
    }
  }

  const labels = isAr
    ? { prompt: 'هل ساعدتك هذه النتيجة؟', helpful: 'نعم', notHelpful: 'لا', wrong: 'خاطئ', thanks: 'شكرًا على ملاحظتك — تساهم في تحسين دقة الفحص.' }
    : { prompt: 'Was this verdict helpful?', helpful: 'Yes', notHelpful: 'No', wrong: 'Inaccurate', thanks: 'Thanks — your feedback makes the engine more accurate.' };

  if (submitted) {
    return (
      <div className="mb-6 rounded-xl bg-emerald/10 border border-emerald/20 p-3 text-center">
        <span className="text-emerald text-lg">✓</span>
        <span className="text-sm text-obsidian-800 ms-2">{labels.thanks}</span>
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-xl bg-obsidian-900/5 border border-obsidian-900/10 p-3 flex items-center justify-between flex-wrap gap-3">
      <div className="text-sm text-obsidian-900 font-medium">{labels.prompt}</div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => send('helpful')}
          disabled={sending}
          className="px-3 py-1.5 rounded-full bg-emerald/10 border border-emerald/30 text-emerald hover:bg-emerald/20 text-xs font-bold tracking-wide transition disabled:opacity-50"
        >
          👍 {labels.helpful}
        </button>
        <button
          onClick={() => send('not_helpful')}
          disabled={sending}
          className="px-3 py-1.5 rounded-full bg-gold/10 border border-gold/30 text-gold-deep hover:bg-gold/20 text-xs font-bold tracking-wide transition disabled:opacity-50"
        >
          👎 {labels.notHelpful}
        </button>
        <button
          onClick={() => send('flagged_wrong')}
          disabled={sending}
          className="px-3 py-1.5 rounded-full bg-rose/10 border border-rose/30 text-rose hover:bg-rose/20 text-xs font-bold tracking-wide transition disabled:opacity-50"
        >
          ✕ {labels.wrong}
        </button>
      </div>
    </div>
  );
}

function Metric({
  label,
  value,
  tone = 'emerald',
}: {
  label: string;
  value: string;
  tone?: 'emerald' | 'gold' | 'rose';
}) {
  const color =
    tone === 'rose' ? 'text-rose bg-rose/10 border-rose/20' : tone === 'gold' ? 'text-gold-deep bg-gold/10 border-gold/20' : 'text-emerald bg-emerald/10 border-emerald/20';
  return (
    <div className={`rounded-xl px-4 py-2 border ${color}`}>
      <div className="text-[9px] tracking-[0.25em] uppercase font-bold opacity-80">{label}</div>
      <div className="font-mono text-lg font-bold">{value}</div>
    </div>
  );
}

// ------------------------------------------------------------------------
// Step: Error
// ------------------------------------------------------------------------

function ErrorStep({
  msg,
  isAr,
  c,
  onAgain,
}: {
  msg: string;
  isAr: boolean;
  c: ReturnType<typeof useCopy>;
  onAgain: () => void;
}) {
  return (
    <div className="rounded-luxe bg-obsidian-700 border border-rose/20 p-8 md:p-12 text-center" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="text-5xl text-rose mb-4">!</div>
      <div className="font-serif text-cream text-2xl mb-2">{c.errorTitle}</div>
      <p className="text-fog-400 text-sm max-w-sm mx-auto">{msg}</p>
      <button
        onClick={onAgain}
        className="mt-6 text-xs tracking-[0.25em] uppercase text-gold-deep font-bold hover:text-gold transition"
      >
        ↻ {c.scanAgain}
      </button>
    </div>
  );
}

// ------------------------------------------------------------------------
// Top ribbon: step indicator
// ------------------------------------------------------------------------

function StepRibbon({
  step,
  isAr,
  c,
}: {
  step: Step;
  isAr: boolean;
  c: ReturnType<typeof useCopy>;
}) {
  const sequence: Step[] = ['brand', 'photos', 'code', 'consent', 'scanning', 'results'];
  const idx = sequence.indexOf(step === 'error' ? 'scanning' : step);
  const steps = [
    { label: isAr ? '١' : '1', title: isAr ? 'ماركة' : 'Brand' },
    { label: isAr ? '٢' : '2', title: isAr ? 'صور' : 'Photos' },
    { label: isAr ? '٣' : '3', title: isAr ? 'الرقم' : 'Code' },
    { label: isAr ? '٤' : '4', title: isAr ? 'موافقة' : 'Consent' },
    { label: isAr ? '٥' : '5', title: isAr ? 'فحص' : 'Scan' },
    { label: isAr ? '٦' : '6', title: isAr ? 'النتيجة' : 'Result' },
  ];
  return (
    <div className="flex items-center gap-0 max-w-2xl">
      {steps.map((s, i) => {
        const done = i <= idx;
        return (
          <div key={i} className="flex items-center flex-1 last:flex-none gap-2">
            <div
              className={`w-8 h-8 rounded-full grid place-items-center text-xs font-bold font-mono transition ${
                done
                  ? 'bg-gradient-to-br from-gold-deep to-gold text-obsidian-900'
                  : 'bg-obsidian-800 text-fog-500 border border-white/5'
              }`}
            >
              {s.label}
            </div>
            <div className={`text-[10px] tracking-[0.25em] uppercase font-bold ${done ? 'text-gold-soft' : 'text-fog-500'} hidden md:block`}>
              {s.title}
            </div>
            {i < steps.length - 1 && (
              <div className={`h-px flex-1 mx-2 ${i < idx ? 'bg-gold/40' : 'bg-white/5'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}
